export const DEFAULT_ERROR_MESSAGE = 'Whoops, something went wrong. Please try again.';

type StashErrorItem = string | { message?: string };

type StashError = {
	displayError?: string;
	errors?: StashErrorItem[] | StashErrorItem;
	body?: StashErrorItem[] | StashErrorItem;
	message?: string;
};

/**
 * Returns a normalized error string from any of the stash error response
 * types.
 */
export const getErrorMessage = (
	error: unknown,
	fallbackMessage: string = DEFAULT_ERROR_MESSAGE,
): string => {
	try {
		const err = error as StashError | null | undefined;
		if (!!err?.displayError) {
			return err.displayError;
		}
		const errors = err && (err.errors || err.body);

		if (!errors) return fallbackMessage;

		if (Array.isArray(errors)) {
			return errors
				.map((item) => {
					return typeof item === 'object' ? item.message : item;
				})
				.join(' ');
		} else if (typeof errors === 'object') {
			return errors?.message ?? fallbackMessage;
		} else {
			return errors;
		}
	} catch {
		return fallbackMessage;
	}
};

export const getErrorCode = (error: unknown): string | undefined => {
	const err = error as { errors?: { code?: string }[] } | null | undefined;
	if (err?.errors && Array.isArray(err.errors) && err.errors.length > 0) {
		return err.errors[0].code;
	}

	return undefined;
};

/**
 * Detects errors thrown when a dynamically-imported module or its CSS fails
 * to load after a Vite deploy (or a dropped network mid-import).
 *
 * stash-web emits hashed Vite modules (`Foo.D7gWZiAn.js`), not webpack
 * `*.chunk.js`. `name === 'ChunkLoadError'` is kept for leftover webpack
 * runtimes. Production failures on stash-web surface as browser TypeErrors:
 *  - Safari/WebKit: `Importing a module script failed.`
 *  - Chrome / Edge: `Failed to fetch dynamically imported module: <url>`
 *  - Firefox: `error loading dynamically imported module`
 *  - Chrome: `is not a valid JavaScript MIME type` when a stale hashed URL
 *    returns HTML (SPA 404) instead of JS
 *  - Vite CSS preload: `Unable to preload CSS for <url>`
 *
 * Matching by message lets the error boundary recover with a single reload
 * instead of dead-ending on the "Whoops, something went wrong" screen.
 */
export const isChunkLoadError = (error: unknown): boolean => {
	if (!error) return false;
	const err = error as { name?: string; message?: unknown };
	if (err.name === 'ChunkLoadError') return true;
	const message = typeof err.message === 'string' ? err.message : '';
	return (
		message.includes('Importing a module script failed') ||
		message.includes('Failed to fetch dynamically imported module') ||
		message.includes('error loading dynamically imported module') ||
		message.includes('is not a valid JavaScript MIME type') ||
		message.includes('Unable to preload CSS')
	);
};
